import { SkeletonTableBody } from 'blocks/data-table/skeleton'
import clsx from 'clsx'
import { Table, type TableProps } from 'primitives/table'
import { useMemo } from 'react'
import { useTableColumnVirtualizer } from '../../hooks/use-column-virtualizer'
import type { DataTableInstance, DataTableRowData } from '../../types'
import { parseCSSVarId } from '../../utils/style.utils'
import { DataTableBody, MemoDataTableBody } from '../body/table-body'
import { DataTableHead } from '../head/table-head'

interface Props<TData extends DataTableRowData> extends TableProps {
  table: DataTableInstance<TData>
}

export const DataTable = <TData extends DataTableRowData>({ table, ...rest }: Props<TData>) => {
  const {
    getFlatHeaders,
    getState,
    options: { columns, enableTableHead, layoutMode, memoMode },
  } = table
  const { columnSizing, columnSizingInfo, columnVisibility, isLoading } = getState()

  const tableProps = {
    table,
    ...rest,
  }

  const columnSizeVars = useMemo(() => {
    const headers = getFlatHeaders()
    const colSizes: { [key: string]: number } = {}
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]
      const colSize = header.getSize()
      colSizes[`--header-${parseCSSVarId(header.id)}-size`] = colSize
      colSizes[`--col-${parseCSSVarId(header.column.id)}-size`] = colSize
    }
    return colSizes
  }, [columns, columnSizing, columnSizingInfo, columnVisibility])

  const columnVirtualizer = useTableColumnVirtualizer(table)

  const commonTableGroupProps = {
    columnVirtualizer,
    table,
  }

  const renderTableBody = () => {
    if (isLoading) {
      return <SkeletonTableBody columnCount={columns.length} rowCount={10} />
    }

    if (memoMode === 'table-body' || columnSizingInfo.isResizingColumn) {
      return <MemoDataTableBody {...commonTableGroupProps} tableProps={tableProps} />
    }

    return <DataTableBody {...commonTableGroupProps} tableProps={tableProps} />
  }

  return (
    <Table
      className={clsx(
        'mrt-table bg-background',
        layoutMode?.startsWith('grid') && 'grid',
        tableProps.className,
      )}
      {...tableProps}
      style={{
        ...columnSizeVars,
      }}
    >
      {enableTableHead && <DataTableHead {...commonTableGroupProps} />}
      {renderTableBody()}
    </Table>
  )
}
